import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { FormHandles, Scope } from '@unform/core'
import {
  Breadcrumb,
  Cabecalho,
  Carregando,
  ErroLayoutContainer,
  FormUnform,
  IconeBack,
  InputUnform,
  Link
} from 'src/componentes'
import { Botao } from 'src/componentes/botao'
import { Requerimento } from 'src/dto'
import { Conteudo } from 'src/paginas/ipgs/split-pagamento/cadastro/styles'
import {
  ModalFinalizarTicket,
  ModalFinalizarTicketRef,
  FinalizarTicket
} from 'src/paginas/ipgs/filas-trabalho/componentes'
import { formatadores } from 'src/paginas/aluno/painel-financeiro/pagina-financeiro/formatadores'
import {
  ContainerCheio,
  ContainerGrande,
  ContainerMedio,
  ContainerPequeno,
  Linha
} from 'src/paginas/matricula/styles'
import { Api } from 'src/servicos'
import {
  SituacaoEmailSecretaria,
  SituacaoRequerimento,
  StatusTicket
} from 'src/tipos'
import {
  SubMenu,
  Container,
  ContainerLink,
  ContainerMain
} from 'src/paginas/aluno/requerimentos/detalhes-requerimento/style'

import { podeReenviarEmailSecretaria } from './email-secretaria'
import {
  AcoesFinalizar,
  CabecalhoDetalhe,
  CabecalhoEndereco,
  FaixaEmail,
  StatusPill
} from './style'

const PaginaDetalheCertificadoImpressoConteudo: React.FC = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const formRef = useRef<FormHandles>(null)
  const modalFinalizarTicket = useRef<ModalFinalizarTicketRef>(null)
  const [requerimento, definirRequerimento] = useState<Requerimento>(null)
  const [pronto, definirPronto] = useState(false)
  const [reenviando, definirReenviando] = useState(false)

  const obterDados = async () => {
    try {
      definirPronto(false)
      const dados = await Api.RequisitarDadosRequerimento(id)
      definirRequerimento(dados)
    } catch {
      toast('Não foi possível carregar a solicitação.', { type: 'error' })
    } finally {
      definirPronto(true)
    }
  }

  useEffect(() => {
    obterDados()
  }, [id])

  const pagamentoConfirmado = Boolean(
    requerimento?.dataPagamentoConfirmado ||
      requerimento?.parcela?.situacao === 'Pago' ||
      requerimento?.parcela?.situacaoParcela === 'Pago'
  )

  const emailEnviado =
    requerimento?.situacaoEmailSecretaria === SituacaoEmailSecretaria.Enviado

  const emailFalhou =
    requerimento?.situacaoEmailSecretaria === SituacaoEmailSecretaria.Falhou

  const emailAntesDaNotificacao =
    pagamentoConfirmado &&
    !emailEnviado &&
    !emailFalhou

  const variacaoFaixa: 'sucesso' | 'erro' | 'espera' = emailEnviado
    ? 'sucesso'
    : emailFalhou
    ? 'erro'
    : 'espera'

  const textoFaixa = emailEnviado
    ? 'E-mail enviado para secretaria.pos@ipgs.com.br'
    : emailFalhou
    ? 'Falha no envio do e-mail para a Secretaria'
    : emailAntesDaNotificacao
    ? 'Pedido anterior à notificação por e-mail'
    : 'O e-mail será enviado após a confirmação do pagamento'

  const detalheFaixa = emailEnviado
    ? `Após a confirmação do pagamento, protocolo ${requerimento?.numeroProtocolo ?? ''}`
    : emailFalhou
    ? 'Use Reenviar e-mail para tentar novamente.'
    : emailAntesDaNotificacao
    ? 'O reenvio fica bloqueado para a Secretaria não receber um pedido já atendido.'
    : 'Solicitações sem pagamento não geram e-mail.'

  const ticketCancelado =
    requerimento?.situacaoRequerimento === SituacaoRequerimento.Cancelado

  const ticketFinalizado =
    requerimento?.statusTicket === StatusTicket.Finalizado

  const podeReenviar = podeReenviarEmailSecretaria(
    requerimento?.situacaoEmailSecretaria,
    pagamentoConfirmado
  )

  const copiarEndereco = async () => {
    const endereco = requerimento?.endereco
    if (!endereco) {
      toast('Endereço não informado.', { type: 'error' })
      return
    }

    const complemento = endereco.complemento
      ? `, ${endereco.complemento}`
      : ''
    const texto = [
      requerimento.nome,
      `${endereco.logradouro}, ${endereco.numero}${complemento}`,
      `${endereco.bairro}, ${endereco.cidade}/${endereco.uf}`,
      `CEP ${endereco.cep}, ${endereco.pais}`
    ]
      .filter(Boolean)
      .join('\n')

    try {
      await navigator.clipboard.writeText(texto)
      toast('Endereço copiado', { type: 'success' })
    } catch {
      toast('Não foi possível copiar o endereço.', { type: 'error' })
    }
  }

  const reenviarEmail = async () => {
    try {
      definirReenviando(true)
      await Api.ReenviarEmailSecretaria(id)
      toast('E-mail reenviado para a Secretaria', { type: 'success' })
      await obterDados()
    } catch {
      toast('Falha ao reenviar o e-mail para a Secretaria.', { type: 'error' })
    } finally {
      definirReenviando(false)
    }
  }

  const finalizarTicket = async (dados: FinalizarTicket) => {
    try {
      await Api.FinalizarTicket(dados.idTicket, dados.observacao)
      toast('Solicitação finalizada. Endereço mantido no histórico', {
        type: 'success'
      })
      await obterDados()
    } catch {
      toast('Erro ao finalizar a solicitação.', { type: 'error' })
    }
  }

  const dadosFormulario = requerimento
    ? {
        ...requerimento,
        dataAbertura: formatadores.dataHoraLocaleString(
          requerimento.dataSolicitacao
        ),
        pagamento: pagamentoConfirmado ? 'Confirmado' : 'Aguardando'
      }
    : {}

  return (
    <>
      <Container>
        <ContainerLink>
          <Link
            texto="Voltar"
            icone={IconeBack}
            acaoVoltar={() => history.goBack()}
          />
        </ContainerLink>
        <ContainerMain>
          <Cabecalho>
            <Breadcrumb
              titulo="Detalhes da solicitação de Certificado Impresso"
              atalhos={[
                { texto: 'Requerimentos' },
                { texto: 'Certificado Impresso' }
              ]}
            />
          </Cabecalho>
          <Conteudo>
            {!pronto || !requerimento ? (
              <Carregando texto="Carregando dados..." />
            ) : (
              <>
                <CabecalhoDetalhe>
                  <StatusPill
                    variacao={
                      ticketCancelado
                        ? 'cancelado'
                        : ticketFinalizado
                        ? 'finalizado'
                        : 'aberto'
                    }
                  >
                    {ticketCancelado
                      ? 'Cancelado'
                      : ticketFinalizado
                      ? 'Finalizado'
                      : 'Aberto'}
                  </StatusPill>
                </CabecalhoDetalhe>
                <FaixaEmail variacao={variacaoFaixa}>
                  <div>
                    <strong>{textoFaixa}</strong>
                    <span>{detalheFaixa}</span>
                  </div>
                  {podeReenviar && (
                    <Botao
                      texto="Reenviar e-mail"
                      tema="Secundario"
                      type="button"
                      carregando={reenviando}
                      onClick={reenviarEmail}
                    />
                  )}
                </FaixaEmail>
                <FormUnform
                  ref={formRef}
                  dadosIniciais={dadosFormulario}
                  acaoSucesso={() => undefined}
                >
                  <Linha>
                    <ContainerCheio>
                      <InputUnform
                        name="nome"
                        label="Nome Completo"
                        disabled
                        style={{ textTransform: 'uppercase' }}
                      />
                    </ContainerCheio>
                  </Linha>
                  <Linha>
                    <ContainerCheio>
                      <InputUnform name="turma.nome" label="Turma" disabled />
                    </ContainerCheio>
                  </Linha>
                  <Linha>
                    <ContainerMedio>
                      <InputUnform
                        name="dataAbertura"
                        label="Aberta pelo aluno em"
                        disabled
                      />
                    </ContainerMedio>
                    <ContainerMedio>
                      <InputUnform
                        name="pagamento"
                        label="Pagamento"
                        disabled
                      />
                    </ContainerMedio>
                  </Linha>
                  <CabecalhoEndereco>
                    <SubMenu>Endereço para envio</SubMenu>
                    <Botao
                      texto="Copiar endereço"
                      tema="Secundario"
                      type="button"
                      onClick={copiarEndereco}
                    />
                  </CabecalhoEndereco>
                  <Scope path="endereco">
                    <Linha>
                      <ContainerGrande>
                        <InputUnform
                          name="logradouro"
                          label="Logradouro"
                          disabled
                        />
                      </ContainerGrande>
                      <ContainerPequeno>
                        <InputUnform name="numero" label="Número" disabled />
                      </ContainerPequeno>
                    </Linha>
                    <Linha>
                      <ContainerMedio>
                        <InputUnform
                          name="complemento"
                          label="Complemento"
                          disabled
                        />
                      </ContainerMedio>
                      <ContainerMedio>
                        <InputUnform name="bairro" label="Bairro" disabled />
                      </ContainerMedio>
                    </Linha>
                    <Linha>
                      <ContainerMedio>
                        <InputUnform name="cidade" label="Cidade" disabled />
                      </ContainerMedio>
                      <ContainerPequeno>
                        <InputUnform name="uf" label="UF" disabled />
                      </ContainerPequeno>
                      <ContainerPequeno>
                        <InputUnform name="cep" label="CEP" disabled />
                      </ContainerPequeno>
                    </Linha>
                  </Scope>
                </FormUnform>
                {!ticketFinalizado &&
                  !ticketCancelado &&
                  requerimento.ticketId && (
                  <AcoesFinalizar>
                    <Botao
                      texto="Finalizar solicitação"
                      type="button"
                      onClick={() =>
                        modalFinalizarTicket.current?.abrir(
                          requerimento.ticketId
                        )
                      }
                    />
                  </AcoesFinalizar>
                )}
              </>
            )}
          </Conteudo>
        </ContainerMain>
      </Container>
      <ModalFinalizarTicket
        id="modal-finalizar-ticket-certificado"
        ref={modalFinalizarTicket}
        backdrop
        acaoPrimaria={finalizarTicket}
      />
    </>
  )
}

export const PaginaDetalheCertificadoImpresso: React.FC = () => (
  <ErroLayoutContainer.Provider>
    <PaginaDetalheCertificadoImpressoConteudo />
  </ErroLayoutContainer.Provider>
)

export default PaginaDetalheCertificadoImpresso
